import React, { useEffect, useRef, useState } from 'react';
import type { IntlLike } from '../clientPresentation';
import { MAX_RECRUIT_SCORE, RECRUIT_AXES } from '../recruitScore';
import type { RecruitAxis, RecruitScores } from '../recruitScore';

/**
 * 候選人評估's pentagon, drawing only — no editing, no storage. Drawn by the
 * drawer's RecruitRadar (where the scores are given) and by 招募 IDEA's
 * 他做得來嗎 card (where they are read), so both show one shape.
 */

// Same pentagon as CoverageRadar: top, then 72° steps clockwise.
const ANGLES: Record<RecruitAxis, number> = { motive: -90, network: -18, comms: 54, discipline: 126, family: 198 };
const CX = 140;
const CY = 120;
const R = 88;
const LABEL_GAP = 16;

const SHAPE_MS = 420;
// Unrated axes sit on a small placeholder so the outline stays a pentagon.
const EMPTY_SCALE = 0.05;
const easeOutCubic = (x: number) => 1 - Math.pow(1 - x, 3);

const pointAt = (angleDeg: number, scale: number) => {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: CX + R * scale * Math.cos(rad), y: CY + R * scale * Math.sin(rad) };
};

const ringPoints = (scale: number) =>
  RECRUIT_AXES.map(axis => {
    const p = pointAt(ANGLES[axis], scale);
    return `${p.x},${p.y}`;
  }).join(' ');

const scalesFor = (scores: RecruitScores) =>
  RECRUIT_AXES.map(axis => {
    const score = scores[axis];
    return score !== undefined ? score / MAX_RECRUIT_SCORE : EMPTY_SCALE;
  });

/**
 * Tweens the five radii toward `target`, from wherever the shape is now — so a
 * tap mid-animation bends the shape rather than snapping it. SVG `points` is
 * not CSS-animatable, hence requestAnimationFrame. Grows in from the centre on
 * mount; reduced-motion users get the end state straight away.
 */
const useAnimatedScales = (target: number[]) => {
  const [shown, setShown] = useState<number[]>(() => target.map(() => 0));
  const shownRef = useRef(shown);
  const key = target.join(',');

  useEffect(() => {
    const reduce =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || typeof window.requestAnimationFrame !== 'function') {
      shownRef.current = target;
      setShown(target);
      return undefined;
    }
    const from = shownRef.current;
    let frame = 0;
    let start: number | null = null;
    const step = (now: number) => {
      if (start === null) start = now;
      const progress = Math.min(1, (now - start) / SHAPE_MS);
      const eased = easeOutCubic(progress);
      const next = target.map((to, i) => from[i] + (to - from[i]) * eased);
      shownRef.current = next;
      setShown(next);
      if (progress < 1) frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(frame);
    // `key` stands in for `target`, which is a fresh array every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return shown;
};

export type RecruitRadarChartProps = {
  scores: RecruitScores;
  intl: IntlLike;
  className?: string;
};

const RecruitRadarChart = (props: RecruitRadarChartProps) => {
  const { scores, intl, className } = props;
  const shownScales = useAnimatedScales(scalesFor(scores));
  const rated = RECRUIT_AXES.some(axis => scores[axis] !== undefined);
  const plots = RECRUIT_AXES.map((axis, i) => ({
    axis,
    score: scores[axis],
    point: pointAt(ANGLES[axis], shownScales[i])
  }));

  return (
    <svg viewBox="0 0 280 216" width="100%" aria-hidden="true" className={className}>
      {[0.2, 0.4, 0.6, 0.8, 1].map(scale => (
        <polygon key={scale} points={ringPoints(scale)} fill="none" stroke="#f1e4d2" />
      ))}
      {RECRUIT_AXES.map(axis => {
        const outer = pointAt(ANGLES[axis], 1);
        return <line key={axis} x1={CX} y1={CY} x2={outer.x} y2={outer.y} stroke="#f5ece0" />;
      })}
      {rated && (
        <polygon
          points={plots.map(plot => `${plot.point.x},${plot.point.y}`).join(' ')}
          fill="rgba(217,119,6,.2)"
          stroke="#d97706"
          strokeWidth={2}
          data-testid="recruit-radar-area"
        />
      )}
      {plots
        .filter(plot => plot.score !== undefined)
        .map(plot => (
          <circle key={plot.axis} cx={plot.point.x} cy={plot.point.y} r={2.8} fill="#b45309" />
        ))}
      {RECRUIT_AXES.map(axis => {
        const label = pointAt(ANGLES[axis], (R + LABEL_GAP) / R);
        const score = scores[axis];
        return (
          <text
            key={axis}
            x={label.x}
            y={label.y}
            fill={score !== undefined ? '#92400e' : '#94a3b8'}
            textAnchor="middle"
            dominantBaseline="middle"
            data-testid="recruit-radar-label"
            data-axis={axis}
          >
            {intl.formatMessage({ id: `clientv2-recruit-axis-${axis}` })}
            {score !== undefined ? ` ${score}` : ''}
          </text>
        );
      })}
    </svg>
  );
};

export default RecruitRadarChart;
